import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { TextField, Select, Button, FormError } from "components";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { deviceTypeOptions, terminalStatuses } from "utils/constants";
import { Formik, Form, ErrorMessage } from "formik";
import { defaultValidation } from "utils/validators";
import { getRegions } from "redux/actions/regionActions";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import { getRegionOptions, isSuccessResponse } from "utils/helper";

const SingleRegistration = ({ registerTerminal }) => {
  const [stateOptions, setStateOptions] = useState([]);

  const location = useLocation();

  const currentTerminal = location?.state?.currentTerminal;

  const statusesToCheck = terminalStatuses.slice(0, 2);

  const {
    regions: { regions },
  } = useSelector((state) => state);

  const getSelectedRegionStates = async (regionName) => {
    try {
      const selectedRegion = regions.find(
        (region) => region.name === regionName
      );

      if (selectedRegion) {
        const { data } = await makeRequest(
          "get",
          routes.fetchRegions(`region=${selectedRegion?.id}&type=1`)
        );
        if (isSuccessResponse(data)) {
          setStateOptions(getRegionOptions(data?.data));
        }
      }
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    getRegions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getSelectedRegionStates(currentTerminal?.region);
    // eslint-disable-next-line
  }, [regions]);

  const canUpdateTerminalStatus = () =>
    statusesToCheck.find(
      (status) =>
        status.mapper === currentTerminal?.status ||
        status.value.toString() === currentTerminal?.status?.toString()
    );

  let initialValues = {
    oem: currentTerminal?.oem || "",
    terminalId: currentTerminal?.terminalId || "",
    serialNumber: currentTerminal?.serialNumber || "",
    deviceType: currentTerminal?.deviceType || "",
    imei: currentTerminal?.imei || "",
    region: currentTerminal?.region || "",
    state: currentTerminal?.state || "",
    terminalAddress: currentTerminal?.terminalAddress || "",
  };

  if (currentTerminal) {
    initialValues = {
      ...initialValues,
      status:
        terminalStatuses.find(
          (status) => status.mapper === currentTerminal?.status
        )?.value || "",
    };
  }

  const validationSchema = Yup.object({
    oem: defaultValidation("OEM Name"),
    terminalId: defaultValidation("Terminal ID"),
    serialNumber: defaultValidation("Serial Number"),
    deviceType: defaultValidation("Device Type"),
    // region: currentTerminal && defaultValidation("Region"),
    // state: currentTerminal && defaultValidation("State"),
    // terminalAddress: currentTerminal && defaultValidation("Terminal Address"),
  });

  return (
    <div className="register-terminal-container">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) =>
          registerTerminal([values], resetForm, currentTerminal?.id)
        }
      >
        {({
          errors,
          touched,
          isSubmitting,
          values,
          setFieldValue,
          getFieldProps,
        }) => (
          <Form noValidate autoComplete="off">
            <div className="row">
              <div className="col-md-6 mb-3">
                <TextField
                  name="oem"
                  type="text"
                  placeholder="Input text"
                  label="OEM Name"
                  {...getFieldProps("oem")}
                  hasError={errors.oem && touched.oem}
                />
                <ErrorMessage component={FormError} name="oem" />
              </div>

              <div className="col-md-6 mb-3">
                <TextField
                  name="terminalId"
                  type="text"
                  placeholder="Input text"
                  label="Terminal ID"
                  {...getFieldProps("terminalId")}
                  hasError={errors.terminalId && touched.terminalId}
                />
                <ErrorMessage component={FormError} name="terminalId" />
              </div>

              <div className="col-md-6 mb-3">
                <TextField
                  name="serialNumber"
                  type="text"
                  placeholder="input text"
                  label="Serial Number"
                  {...getFieldProps("serialNumber")}
                  hasError={errors.serialNumber && touched.serialNumber}
                />
                <ErrorMessage component={FormError} name="serialNumber" />
              </div>

              <div className="col-md-6 mb-3">
                <Select
                  label="Device Type"
                  placeholder="Select type"
                  options={deviceTypeOptions}
                  name="deviceType"
                  {...getFieldProps("deviceType")}
                  hasError={errors.deviceType && touched.deviceType}
                />
                <ErrorMessage component={FormError} name="deviceType" />
              </div>

              <div
                className={`mb-3 ${
                  canUpdateTerminalStatus() ? "col-md-6" : ""
                }`}
              >
                <TextField
                  name="imei"
                  type="text"
                  placeholder="Input text"
                  label="Device IMEI"
                  required={false}
                  {...getFieldProps("imei")}
                />
              </div>

              {canUpdateTerminalStatus() && (
                <div className="col-md-6 mb-3">
                  <Select
                    label="Update Status"
                    required={false}
                    options={statusesToCheck.map((status) => ({
                      label: status.label,
                      value: status.value,
                    }))}
                    name="status"
                    onChange={(e) => setFieldValue("status", e.target.value)}
                    value={values.status}
                  />
                </div>
              )}

              {/* {currentTerminal && ( */}
              <>
                <div className="col-md-6 mb-3">
                  <Select
                    label="Region"
                    placeholder="Select Region"
                    options={getRegionOptions(regions)}
                    loading={!regions.length}
                    required={false}
                    name="region"
                    onChange={(e) => {
                      let val = e.target.value;
                      setStateOptions([]);
                      setFieldValue("state", "");
                      setFieldValue("region", val);
                      getSelectedRegionStates(val);
                    }}
                    value={values.region}
                    hasError={errors.region && touched.region}
                  />
                  <ErrorMessage component={FormError} name="region" />
                </div>

                <div className="col-md-6 mb-3">
                  <Select
                    label="State"
                    placeholder="Select state"
                    required={false}
                    options={stateOptions}
                    disabled={!values.region}
                    name="state"
                    {...getFieldProps("state")}
                    hasError={errors.state && touched.state}
                  />
                  <ErrorMessage component={FormError} name="state" />
                </div>
                <div className="mb-3">
                  <TextField
                    name="terminalAddress"
                    required={false}
                    type="text"
                    placeholder="Input Terminal Address"
                    label="Terminal Address"
                    {...getFieldProps("terminalAddress")}
                  />
                  <ErrorMessage component={FormError} name="terminalAddress" />
                </div>
              </>
              {/* )} */}
              <div className="register-terminal-cta">
                <Button
                  type="submit"
                  loading={isSubmitting}
                  label={`${currentTerminal ? "Update" : "Register"} Terminal`}
                  fullWidth
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SingleRegistration;
